import cx from "classnames";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { useTransition, animated } from "@react-spring/web";

import Thumbnail from "../thumbnail";
import Tooltip from "../tooltip";
import { FilterView } from "../banner";
import DescriptionEditor from "src/pages/settings/accountManagement/components/description";

import { useUserDetails } from "src/utils/collections.helper";
import CollectionWidgetManager from "src/utils/collections.widget.service";

import styles from "./styles.module.scss";

const DefaultBanner = "/images/collections/background.jpeg";
const DefaultThumbnail = "/images/default_user_profile_background.png";

const useUser = () => {
  const user = useSelector((state: any) => get(state, "users.me", {}));
  return useUserDetails(user);
};

interface ExperienceProps {
  banner: any;
  editorContent: string;
  onChange: (templateValue: any) => void;
  onMenuChange: (newState: any) => void;
}

interface BannerExperienceProps extends ExperienceProps {
  viewerType: "MASONRY" | "BENTO_GRID" | "CAROUSEL" | "DEAL_ROOM";
}

interface BannerWrapperProps {
  banner: any;
  children: React.ReactElement;
}

const ContactCard = ({ user }: { user: ReturnType<typeof useUser> }) => {
  const {
    userName,
    userTitle,
    userEmail,
    userImage,
    userContactNumber,
    linkedInUrl,
  } = user;

  return (
    <div className={cx(styles.card, styles.contactCard)}>
      <div className={styles.icon}>
        <div
          className={styles.contactCardImage}
          style={{
            backgroundImage: `url('${userImage || DefaultThumbnail}')`,
          }}
        />
        <div className={styles.vignetteBlur} />
      </div>

      <div className={styles.cardContent}>
        <div className={styles.nameAndTitle}>
          <div className={styles.nameContainer}>
            <Tooltip content={userName} placement="top">
              <p
                className={cx(styles.name, {
                  [styles.fullWidth]: !linkedInUrl,
                })}
              >
                {userName}
              </p>
            </Tooltip>

            {linkedInUrl && (
              <div className={styles.linkedIn}>
                <i
                  className={cx(
                    styles.icon,
                    "paperflite-linkedin",
                    styles.linkedInIcon,
                  )}
                />
              </div>
            )}
          </div>

          {userTitle && <p className={styles.title}>{userTitle}</p>}
        </div>

        <div className={styles.ctaParentContainer}>
          {userEmail && (
            <div className={styles.contentWrapper}>
              <div className={cx(styles.ctaContainer, styles.mail)}>
                <i className={cx(styles.icon, "paperflite-mail")} />
                <p className={styles.content}>{userEmail}</p>
              </div>
            </div>
          )}

          {userContactNumber && (
            <div className={styles.ctaContainer}>
              <i className={cx(styles.icon, "paperflite-phone-call")} />
              <p className={styles.content}>{userContactNumber}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const BannerWrapper = ({ banner, children }: BannerWrapperProps) => {
  return (
    <div className={styles.bannerWrapper}>
      <Thumbnail
        media={banner?.background}
        defaultImage={DefaultBanner}
        crop={banner?.crop}
        position={banner?.position}
      />
      <FilterView banner={banner} />
      {children}
    </div>
  );
};

const MetroExperience = ({
  banner,
  editorContent,
  onChange,
  onMenuChange,
}: ExperienceProps) => {
  const user = useUser();

  return (
    <div className={styles.metroExperience}>
      <BannerWrapper banner={banner}>
        <div className={styles.descriptionContainer}>
          <DescriptionEditor
            editorContent={editorContent}
            widgetManager={new CollectionWidgetManager()}
            maxHeight="none"
            menuState={{ primaryAction: "view" }}
            onChange={onChange}
            onMenuChange={onMenuChange}
          />
        </div>
      </BannerWrapper>

      <div className={styles.contentContainer}>
        <div className={styles.metro}>
          <div className={styles.col}>
            <ContactCard user={user} />
            <div className={cx(styles.card, styles.xl)} />
          </div>

          <div className={styles.col}>
            <div className={cx(styles.card, styles.sm)} />
            <div className={cx(styles.card, styles.sm)} />
          </div>

          <div className={styles.col}>
            <div className={cx(styles.card, styles.md)} />
            <div className={cx(styles.card, styles.sm)} />
          </div>

          <div className={styles.col}>
            <div className={cx(styles.card, styles.xl)} />
            <div className={cx(styles.card, styles.xl)} />
          </div>
        </div>
      </div>
    </div>
  );
};

const BentoExperience = ({
  banner,
  editorContent,
  onChange,
  onMenuChange,
}: ExperienceProps) => {
  const user = useUser();

  return (
    <div className={styles.bentoExperience}>
      <BannerWrapper banner={banner}>
        <div className={styles.grid}>
          <div className={styles.hero}>
            <div className={styles.descriptionContainer}>
              <DescriptionEditor
                editorContent={editorContent}
                widgetManager={new CollectionWidgetManager()}
                maxHeight="none"
                menuState={{ primaryAction: "view" }}
                onChange={onChange}
                onMenuChange={onMenuChange}
              />
            </div>
          </div>

          <div className={styles.cardContainer}>
            <div className={cx(styles.inner, styles.regularContactCard)}>
              <ContactCard user={user} />
            </div>
          </div>

          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className={cx(styles.card, {
                [styles.tall]: i === 1,
                [styles.wide]: i === 7,
              })}
            />
          ))}
        </div>
      </BannerWrapper>
    </div>
  );
};

const CarouselExperience = ({
  banner,
  editorContent,
  onChange,
  onMenuChange,
}: ExperienceProps) => {
  const user = useUser();

  return (
    <div className={styles.carouselExperience}>
      <BannerWrapper banner={banner}>
        <>
          <div className={styles.descriptionContainer}>
            <DescriptionEditor
              editorContent={editorContent}
              widgetManager={new CollectionWidgetManager()}
              maxHeight="none"
              menuState={{ primaryAction: "view" }}
              onChange={onChange}
              onMenuChange={onMenuChange}
            />
          </div>

          <div className={styles.cardLayout}>
            <ContactCard user={user} />
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={styles.card} />
            ))}
          </div>
        </>
      </BannerWrapper>
    </div>
  );
};

const BannerExperienceView = ({
  banner,
  viewerType,
  editorContent,
  onChange,
  onMenuChange,
}: BannerExperienceProps) => {
  const transitions = useTransition(viewerType, {
    key: viewerType,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    trail: 200,
    config: {
      duration: 300,
      tension: 200,
      friction: 20,
    },
    exitBeforeEnter: true,
  });

  const renderMap = {
    MASONRY: MetroExperience,
    BENTO_GRID: BentoExperience,
    CAROUSEL: CarouselExperience,
  };

  return transitions((style, item) => {
    const Component = renderMap[item];

    return (
      <animated.div
        key={item}
        className={styles.experienceContainer}
        style={style}
      >
        {Component && (
          <Component
            banner={banner}
            editorContent={editorContent}
            onChange={onChange}
            onMenuChange={onMenuChange}
          />
        )}
      </animated.div>
    );
  });
};

export default BannerExperienceView;
